import React from 'react';
import 'banner-pattern/dist/main.css';
import { buildClassPrefix, formatErrorMessage } from '../../utils/errorPatterns';

interface AdminTuitionErrorBannerProps {
    currentStep: number;
    errors: Record<string, string>;
    fieldLabels?: Record<string, string>;
    onFieldClick?: (fieldName: string) => void;
    formData?: Record<string, any>;
}

// ==================== UTILITY TYPES ====================

interface ClassFieldMatch {
    isClassField: boolean;
    baseFieldName: string;
    classIndex: number;
    classNumber: number;
}

interface ElementSelector {
    id: string;
    dateInputId: string;
    textareaId: string;
    nameAttr: string;
}



// ==================== UTILITY FUNCTIONS ====================

/**
 * Parse field name to extract class information
 * e.g., "courseName_class1" -> { isClassField: true, baseFieldName: "courseName", classIndex: 1, classNumber: 2 }
 */
const parseClassField = (fieldName: string): ClassFieldMatch => {
    const classRegex = /^(.+)_class(\d+)$/;
    const classMatch = classRegex.exec(fieldName);

    if (classMatch) {
        const classIndex = Number.parseInt(classMatch[2], 10);
        return {
            isClassField: true,
            baseFieldName: classMatch[1],
            classIndex,
            classNumber: classIndex + 1,
        };
    }

    return {
        isClassField: false,
        baseFieldName: fieldName,
        classIndex: -1,
        classNumber: 1,
    };
};

/**
 * Generate selector combinations for finding field elements
 */
const generateSelectors = (fieldName: string): ElementSelector => ({
    id: fieldName,
    dateInputId: `date-input-${fieldName}`,
    textareaId: `textarea-${fieldName}`,
    nameAttr: fieldName,
});

/**
 * Find field element using selector strategy pattern
 */
const findFieldElement = (
    fieldName: string,
    searchContext: Document | HTMLElement = document
): HTMLElement | null => {
    const selectors = generateSelectors(fieldName);

    if (searchContext instanceof Document) {
        return (
            searchContext.getElementById(selectors.id) ||
            searchContext.getElementById(selectors.dateInputId) ||
            searchContext.getElementById(selectors.textareaId) ||
            searchContext.querySelector(`[name="${selectors.nameAttr}"]`)
        );
    }

    return (
        searchContext.querySelector(`#${selectors.id}`) ||
        searchContext.querySelector(`#${selectors.dateInputId}`) ||
        searchContext.querySelector(`#${selectors.textareaId}`) ||
        searchContext.querySelector(`[name="${selectors.nameAttr}"]`)
    );
};

/**
 * Focus on an element, handling different element types appropriately
 */
const focusElement = (element: HTMLElement): void => {
    // Direct focus for native form elements
    if (['SELECT', 'INPUT', 'TEXTAREA'].includes(element.tagName)) {
        element.focus();
        return;
    }

    // For container elements, find and focus nested form element
    const formElement =
        element.querySelector('input') ||
        element.querySelector('select') ||
        element.querySelector('textarea');

    if (formElement) {
        (formElement as HTMLElement).focus();
    } else {
        element.focus();
    }
};

/**
 * Validate date format (MM/DD/YYYY)
 */
const isValidDateFormat = (dateString: string): boolean => {
    if (!dateString) return false;
    const dateRegex = /^(0?\d|1[0-2])\/(0?\d|[12]\d|3[01])\/\d{4}$/;
    return dateRegex.test(String(dateString));
};

/**
 * Scroll to and focus on field element
 */
const scrollToField = (fieldName: string): void => {
    const classFieldInfo = parseClassField(fieldName);
    let targetElement: HTMLElement | null = null;

    if (classFieldInfo.isClassField) {
        // Search within class section
        const classSection = document.getElementById(`class-section-${classFieldInfo.classIndex}`);
        if (classSection) {
            targetElement = findFieldElement(classFieldInfo.baseFieldName, classSection);
        }
    } else {
        // Search globally
        targetElement = findFieldElement(fieldName);
    }

    if (targetElement) {
        targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
        focusElement(targetElement);
    }
};



/**
 * Transform raw error into display-ready error info
 */
interface ErrorInfo {
    fieldName: string;
    fieldLabel: string;
    errorMessage: string;
    classFieldInfo: ClassFieldMatch;
    displayMessage: string;
}

const createErrorInfo = (
    fieldName: string,
    errorMessage: string,
    fieldLabels: Record<string, string>,
    currentStep: number
): ErrorInfo => {
    const classFieldInfo = parseClassField(fieldName);
    const baseFieldName = classFieldInfo.baseFieldName;
    const fieldLabel = fieldLabels[baseFieldName] || baseFieldName;
    const classPrefix = buildClassPrefix(classFieldInfo.isClassField, classFieldInfo.classNumber, currentStep);
    const displayMessage = formatErrorMessage(errorMessage, fieldLabel, classPrefix);

    return {
        fieldName,
        fieldLabel,
        errorMessage,
        classFieldInfo,
        displayMessage,
    };
};

/**
 * Filter errors to remove eligibility error
 */
const filterFieldErrors = (errors: Record<string, string>): Record<string, string> => {
    const { eligibility, ...fieldErrors } = errors;
    return fieldErrors;
};

/**
 * Check if eligibility error should be displayed
 */
const shouldDisplayEligibilityError = (
    errors: Record<string, string>,
    courseStartDate: string
): boolean => {
    return 'eligibility' in errors && isValidDateFormat(courseStartDate);
};

// ==================== REUSABLE COMPONENTS ====================

/**
 * Error list item component
 */
interface ErrorListItemProps {
    errorInfo: ErrorInfo;
    onFieldClick?: (fieldName: string) => void;
}

const ErrorListItem: React.FC<ErrorListItemProps> = ({ errorInfo, onFieldClick }) => (
    <button
        key={errorInfo.fieldName}
        type="button"
        onClick={() => {
            scrollToField(errorInfo.fieldName);
            onFieldClick?.(errorInfo.fieldName);
        }}
        className="!text-[#0074AD] underline text-left w-full"
        style={{
            background: 'none',
            border: 'none',
            padding: 0,
            cursor: 'pointer',
        }}
    >
        <ul className="flex items-start list-disc !m-0 !ml-4">
            <li>
                <div className="flex-1">
                    <span className="text-[16px] leading-[24px]">
                        {errorInfo.displayMessage}
                    </span>
                </div>
            </li>
        </ul>
    </button>
);

/**
 * Error list component
 */
interface StandardErrorListProps {
    errors: Record<string, string>;
    currentStep: number;
    fieldLabels: Record<string, string>;
    onFieldClick?: (fieldName: string) => void;
}

const StandardErrorList: React.FC<StandardErrorListProps> = ({
    errors,
    currentStep,
    fieldLabels,
    onFieldClick,
}) => (
    <div className="mt-2 flex flex-col">
        {Object.entries(errors).map(([fieldName, errorMessage]) => {
            const errorInfo = createErrorInfo(fieldName, errorMessage, fieldLabels, currentStep);
            return (
                <ErrorListItem key={fieldName} errorInfo={errorInfo} onFieldClick={onFieldClick} />
            );
        })}
    </div>
);

/**
 * Banner title component
 */
const BannerTitle: React.FC = () => (
    <div
        className="ds-banner__main-header--awareness"
        id="admin-tuition-error-banner-title"
        style={{ fontWeight: 350, fontSize: '20px' }}
    >
        There are errors that need your attention before submission:
    </div>
);

/**
 * Banner subtitle component
 */
interface BannerSubtitleProps {
    showEligibilityError: boolean;
    eligibilityMessage: string;
}

const BannerSubtitle: React.FC<BannerSubtitleProps> = ({
    showEligibilityError,
    eligibilityMessage,
}) => (
    <p className="p3 !mb-0">
        {showEligibilityError
            ? eligibilityMessage
            : 'Some fields contain invalid or missing information. Review the details below and update the highlighted fields to proceed.'}
    </p>
);

// ==================== MAIN COMPONENT ====================

const AdminTuitionErrorBanner: React.FC<AdminTuitionErrorBannerProps> = ({
    currentStep,
    errors,
    fieldLabels = {},
    onFieldClick,
    formData = {},
}) => {
    const errorCount = Object.keys(errors).length;

    // Early return if no errors
    if (errorCount === 0) {
        return null;
    }

    // Determine eligibility error state
    const courseStartDate = formData?.courseStartDate ?? '';
    const showEligibilityError = shouldDisplayEligibilityError(errors, courseStartDate);
    const eligibilityMessage = showEligibilityError ? errors.eligibility : '';

    // Get field errors (excluding eligibility)
    const fieldErrors = filterFieldErrors(errors);

    // Determine if we should show field error list
    const showFieldErrors = Object.keys(fieldErrors).length > 0;

    return (
        <div className="mb-6">
            <div
                className="ds-banner ds-banner__bg--white ds-banner--icon p-6!"
                data-ds-variant="basic"
                data-ds-version="1"
                data-ds-theme="vessel"
                aria-labelledby="admin-tuition-error-banner-title"
                style={{ borderColor: '#D1480C' }}
            >
                <div className="ds-banner__icon-img">
                    <img
                        src="/assets/images/errorCircleSmall.svg"
                        alt=""
                        style={{ width: '42px', height: '42px' }}
                    />
                </div>

                <div className="ds-banner__container">
                    <div className="ds-banner__main flex flex-col">
                        <BannerTitle />

                        <BannerSubtitle
                            showEligibilityError={showEligibilityError}
                            eligibilityMessage={eligibilityMessage}
                        />

                        {showFieldErrors && (
                            <div className="ds-banner__main-body" style={{ fontSize: '16px' }}>
                                <StandardErrorList
                                    errors={fieldErrors}
                                    currentStep={currentStep}
                                    fieldLabels={fieldLabels}
                                    onFieldClick={onFieldClick}
                                />
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AdminTuitionErrorBanner;